/**
 * @file The PDF intake job ledger: `pdfIntakeJobs/{jobId}`, one document per
 * received PDF, its `events` audit subcollection, and the tasks sent to
 * `/runlanguagemodel`: the review tasks S2 sends and the start task a dropped
 * PDF sends. Written only by the server (the intake
 * orchestrator and the review tasks, through the admin SDK); read by S1's
 * queue and S2's review screen. `StartPdfIntakeToolResult` is what the
 * agent's `startPdfIntake` tool answers. Invariants: `jobId` is the file's
 * `fileSha256`, so the same bytes received again open the earlier job, for
 * whatever order they arrive with, unless that job `failed`, its run's lease
 * lapsed, or it is `ready` with a retryable failed page, when the new receipt
 * claims the job and runs it again; the
 * file lives at `buildFileFolder(uid, fileFolderId)`; `route` is null until
 * verification finishes and is the worst route of any field (`review` over
 * `escalated` over `auto_verified`); `queue` is derived from `status` and
 * `route` by `pdfIntakeJobQueue` and written with them on every server write;
 * field status is derived, never stored; `source`, set only on a job opened
 * from an attachment, is written when the job is first opened and kept by
 * every duplicate and re-claim; `dismissedFrom` is the status a dismissed job
 * returns to on Restore, so Restore never infers it from `stage`.
 */
import type { DocId, TimeType } from './System.types';
import type { ExtractionSourceTypeId, ProcessDocumentDecisions, ProcessWholeDocument } from './System.Agent.Extraction.types';
import type { CellEdit, PdfIntakeRoute } from './System.Agent.PdfEvidence.types';
import type { PricebookRecheckDryRun } from './System.Pricebook.types';

export const PDF_INTAKE_JOBS_COLLECTION = 'pdfIntakeJobs';

export function pdfIntakeJobPath(jobId: string): string {
  return `${PDF_INTAKE_JOBS_COLLECTION}/${jobId}`;
}

export function pdfIntakeJobEventsPath(jobId: string): string {
  return `${PDF_INTAKE_JOBS_COLLECTION}/${jobId}/events`;
}

/**
 * How long a claimed run holds a `processing` job: one hour, the longest
 * request Cloud Run serves, so a lapsed lease always belongs to a run that is
 * gone. The window lives in code, not in the document.
 */
export const PDF_INTAKE_LEASE_MS = 3_600_000;

/**
 * How long a claimed approval holds an `approving` job: one hour, the
 * `runlanguagemodel` service's `--timeout` (set in `deploy.sh` and the deploy
 * workflow), the same window as `PDF_INTAKE_LEASE_MS`, so a lapsed approval
 * lease belongs to a request that is gone. Only a lapsed claim is taken over;
 * a live one, of any generation, is never shared.
 */
export const PDF_INTAKE_APPROVAL_LEASE_MS = 3_600_000;

export type PdfIntakeJobKind = 'schedule' | 'pricebook';

/** The pipeline step the job is in. `done` once mapping has staged the result. */
export type PdfIntakeStage = 'evidence' | 'pageSort' | 'extraction' | 'verification' | 'mapping' | 'done';

/**
 * The job's lifecycle. `ready`: the pipeline finished and the result waits on
 * a person. `approving`: an approval is writing the schedule to its order
 * form, so no save lands; it ends `approved`, or back at `ready` when the
 * approval is refused or fails. `approved`: a schedule was written to its
 * order form, and never returns to `ready`. `dismissed`: a person set a
 * `ready` or `failed` schedule job aside; Restore returns it to the status in
 * `dismissedFrom`. Pricebook jobs stop at `ready` and are never dismissed;
 * their edition carries the publish state.
 */
export type PdfIntakeJobStatus = 'processing' | 'ready' | 'approving' | 'approved' | 'failed' | 'dismissed';

/**
 * The S1 queue view a job is listed under, written by the server with every
 * change of `status` or `route` (`pdfIntakeJobQueue`), so each view is one
 * equality filter. All is every job and has no value here. `dismissed` is
 * listed only under All; no view filters on it.
 */
export type PdfIntakeQueue = 'needsReview' | 'processing' | 'verified' | 'dismissed';

/** One page the pipeline could not finish. S1 and S2 show the job and page, never a storage path. */
export type PdfIntakePageFailure = {
  /** 1-based. */
  page: number;
  reason: string;
  /** True when re-running the page can succeed, so the UI offers Retry page N. */
  retryable: boolean;
};

/** "Actor — thing": who the job waits on and for what, such as "Purchasing — sign-off". */
export type PdfIntakeWaitingOn = {
  actor: 'you' | 'purchasing' | 'pipeline';
  text: string;
};

/** The order form a schedule is written to. */
export type PdfIntakeOrderRef = {
  formId: DocId;
  formVersionId: DocId;
};

/** Where the job's PDF came from when it was not a dropped file: one attachment version, written when the job is first opened; a duplicate or re-claim keeps the first. */
export type PdfIntakeJobSource = {
  kind: 'attachment';
  fileUid: string;
  versionId: string;
  versionNumber: number;
  fileName: string;
};

type PdfIntakeJobBase = {
  /** The job id, equal to `fileSha256`. */
  docId: DocId;
  /** Lowercase hex SHA-256 of the original: `original.pdf`, or `original.xlsx` for a spreadsheet pricebook. */
  fileSha256: string;
  status: PdfIntakeJobStatus;
  /** Null until verification finishes. */
  route: PdfIntakeRoute | null;
  /** Derived from `status` and `route` by `pdfIntakeJobQueue`; never written on its own. */
  queue: PdfIntakeQueue;
  stage: PdfIntakeStage;
  pagesFinished: number;
  /** Null until the evidence stage has counted the pages. */
  pageCount: number | null;
  pageFailures: PdfIntakePageFailure[];
  /** Null when no one is waiting: the job is approved and nothing is left to do. */
  waitingOn: PdfIntakeWaitingOn | null;
  /** Owner of the file folder `temp/<uid>/<fileFolderId>`. */
  uid: string;
  fileFolderId: string;
  /** The file name as received, shown in S1's File column. */
  filename: string;
  /** The `extractionSourceConfigs` entry whose `targetFields` set the field roles. */
  sourceTypeId: ExtractionSourceTypeId;
  receivedAt: TimeType;
  /**
   * Server time the current run claimed the job. A `processing` job is
   * re-claimable once `PDF_INTAKE_LEASE_MS` has passed since then.
   */
  leasedAt: TimeType;
  /** The attachment version the PDF was read from; absent on a dropped file. */
  source?: PdfIntakeJobSource;
  /** The status a dismissed job returns to on Restore; absent on a job never dismissed, null once restored. */
  dismissedFrom?: 'ready' | 'failed' | null;
};

/**
 * A schedule job. `processIndex` names the staging at `process/<n>.json`
 * that S2 reviews, null until mapping stages it. `manufacturerIds` (the
 * entity record ids the reference step resolved for the column with role
 * `manufacturer`) and `catalogNumbers` (the cited catalog numbers, as their
 * cells read, of the fields with role `catalogNumber`) are written when the
 * schedule stages and with every save of its overlay, and serve the
 * open-schedule queries of the publish dry run, the comparison and the
 * re-check.
 */
export type PdfIntakeScheduleJob = PdfIntakeJobBase & {
  kind: 'schedule';
  /**
   * The order form the schedule is for, set when the job is opened, so a
   * retry re-reads it for the same order. Null only on a job stored before
   * it was set at open; `processIndex` alone says whether it is staged.
   */
  order: PdfIntakeOrderRef | null;
  processIndex: number | null;
  manufacturerIds: string[];
  catalogNumbers: string[];
  /**
   * The decisions overlay whose every change has an `edit` event: written in
   * the same transaction as a save's events, so a save whose events failed is
   * audited by the next save, which compares against this rather than the
   * overlay in Storage. Null until a save changes a value; a re-claim resets
   * it with the new staging.
   */
  auditedDecisions: ProcessDocumentDecisions | null;
  /**
   * The staging generation `auditedDecisions` was written at, by a save or by
   * an approval's seal. A save's events land only when the generation it
   * wrote is newer, so a save whose transaction lands after a later save's is
   * refused and never rewinds the audit. Storage generations only grow for one
   * object. Null until a save or approval is audited; a re-claim resets it.
   */
  auditedGeneration: string | null;
  /**
   * When the running approval claimed the job, the approval's lease:
   * its every later write requires it unchanged. An `approving` job is
   * re-claimable by another approval, of any generation, only once
   * `PDF_INTAKE_APPROVAL_LEASE_MS` has passed since then, and the takeover
   * writes a new lease. Null until an approval runs.
   */
  approvingAt: TimeType | null;
  /** The staging generation the running (or last) approval checked; a retry sending it takes over that approval once its lease lapsed. */
  approvingGeneration: string | null;
  /**
   * The published pricebook editions the staging's catalog grammar check last
   * ran against, sorted: exactly the editions published for `manufacturerIds`
   * at the time, written when the schedule stages and by the re-check, each
   * time after the staging it describes. The check is current exactly while
   * this equals the editions published for `manufacturerIds` now; approval
   * refuses otherwise and re-checks the schedule first. A schedule is
   * Re-checking while an edition published for one of its `manufacturerIds`
   * has a running re-check and is not listed here. Absent on a job staged
   * before Phase 3, which reads as none.
   */
  catalogCheckedEditionIds?: string[];
  /**
   * The manufacturers the staging's catalog grammar check last ran for,
   * sorted, written with `catalogCheckedEditionIds`. The check reads which
   * manufacturers have no published edition, so it is current only while this
   * also equals `manufacturerIds` (`scheduleCatalogCheckCurrent`); a save that
   * re-resolves a manufacturer makes it stale. Absent reads as none.
   */
  catalogCheckedManufacturerIds?: string[];
  /**
   * How the last catalog check that moved to new editions changed the
   * schedule (`scheduleCatalogShift`, from the editions it was checked
   * against before to `editionIds`), written in the same transaction as
   * `catalogCheckedEditionIds`, so a re-check that finds the check already
   * current (a duplicate, resumed or racing delivery) counts the schedule as
   * the run that moved it did. Absent until a check moved.
   */
  catalogRecheck?: { editionIds: string[]; shift: ScheduleCatalogShift };
};

/**
 * How one schedule's catalog grammar check moves from one catalog to another,
 * over its cited catalog numbers: `becomesFlagged` when a number that did not
 * fail fails; else `changed` when any number's result moves (a failure
 * cleared, a skipped check now running); else `staysAsIs`. Results compare by
 * `result` alone, so a failure that now names the newer edition stays as it
 * is. The one reading the publish dry run, the re-check and S5 share.
 */
export type ScheduleCatalogShift = 'staysAsIs' | 'becomesFlagged' | 'changed';

/**
 * A schedule job's `process/<n>.json`. `approvedGeneration` is the approval's
 * seal: the staging generation the approval checked, written back with the
 * same content under a generation precondition, so a save still holding that
 * generation is refused by Storage. A save drops it.
 */
export type ScheduleStaging = ProcessWholeDocument & { approvedGeneration?: string };

/**
 * A pricebook job. `editionId` names its edition, null until the pricebook
 * intake creates it, and then equal to the job id, since both are the file's
 * SHA-256. The edition's publish, supersede and re-check events are appended
 * to this job's `events`.
 */
export type PdfIntakePricebookJob = PdfIntakeJobBase & {
  kind: 'pricebook';
  editionId: string | null;
};

/** One document at `pdfIntakeJobs/{jobId}`, discriminated on `kind`. */
export type PdfIntakeJob = PdfIntakeScheduleJob | PdfIntakePricebookJob;

/**
 * What an edit changed, in the decisions overlay's own keys. `cell`: one
 * value, `rowIndex` into the staged table's rows and `tableId` the staged
 * table's group id (`TablePlan.ref.id`). `field`: one header field's value.
 * `column`: one source column's target field, where `from` and `to` are field
 * ids and null is unmapped.
 */
export type PdfIntakeEditTarget =
  | { kind: 'cell'; tableId: string; rowIndex: number; sourceColumn: string }
  | { kind: 'field'; targetField: string }
  | { kind: 'column'; tableId: string; sourceColumn: string };

/**
 * One document at `pdfIntakeJobs/{jobId}/events/{eventId}`, discriminated on
 * `kind`. Pipeline events carry `by: null`. `retry` records who asked for the
 * file to be read again and which retryable failed pages it was for. The
 * edition events go on the pricebook job whose id is the edition's:
 * `publishEdition` names the edition, its source hash, the edition it
 * superseded (null for a first edition) and how many open schedules the
 * re-check will cover; `supersede` goes on the superseded edition's job and
 * names the newer edition; `recheck` with scope `edition` is appended when the
 * re-check finishes, with its final counts. On a schedule job, `recheck` with
 * scope `schedule` records each catalog check that moved to newer editions
 * (a publish's re-check, or an approval or run that found the check out of
 * date): the editions and how the schedule's check moved. `dismiss` records
 * who set a schedule job aside and the status it left; `restore` who brought
 * it back and the status it returned to.
 */
export type PdfIntakeAuditEvent =
  | { docId: DocId; kind: 'extract'; by: null; at: TimeType; pages: number; tables: number; calls: number }
  | { docId: DocId; kind: 'route'; by: null; at: TimeType; route: PdfIntakeRoute; reason: string }
  | ({ docId: DocId; kind: 'edit'; target: PdfIntakeEditTarget } & CellEdit)
  | { docId: DocId; kind: 'approveSchedule'; by: string; at: TimeType; order: PdfIntakeOrderRef }
  | { docId: DocId; kind: 'retry'; by: string; at: TimeType; pages: number[] }
  | {
      docId: DocId;
      kind: 'publishEdition';
      by: string;
      at: TimeType;
      editionId: string;
      sourceSha256: string;
      supersededEditionId: string | null;
      recheckTotal: number;
    }
  | { docId: DocId; kind: 'supersede'; by: string; at: TimeType; supersededByEditionId: string }
  | ({ docId: DocId; kind: 'recheck'; scope: 'edition'; by: null; at: TimeType; editionId: string; total: number; skipped: number } & PricebookRecheckDryRun)
  | {
      docId: DocId;
      kind: 'recheck';
      scope: 'schedule';
      by: null;
      at: TimeType;
      /** The editions the catalog numbers are checked against now, sorted. */
      editionIds: string[];
      shift: ScheduleCatalogShift;
      /** Whether a catalog number the overlay has not overridden fails the check now. */
      flagged: boolean;
    }
  | { docId: DocId; kind: 'dismiss'; by: string; at: TimeType; from: 'ready' | 'failed' }
  | { docId: DocId; kind: 'restore'; by: string; at: TimeType; to: 'ready' | 'failed' };

/**
 * The review tasks S2 sends, and the start task a dropped PDF sends, as
 * `request.data` to `/runlanguagemodel`. Each review task carries `generation`, the Storage generation of the `process/<n>.json` the
 * reviewer loaded, and is refused with a conflict when the staging has moved
 * since, so a reviewer never overwrites or approves a change they have not
 * seen. `savePdfIntakeDecisions` replaces the staging's decisions overlay and
 * appends one `edit` event per change against `auditedDecisions`.
 * `approvePdfIntakeSchedule` claims the job `approving`, refuses while any
 * page failed retryably (retried by sending the file again; a page that cannot
 * be read is entered on the order form by hand and does not block), any cell
 * is flagged or any mapping the overlay has not set sits below
 * `CONFIDENCE_HIGH_THRESHOLD`; then seals the checked staging, writes it onto
 * the order form through `applyToDocument` and sets the job `approved` with
 * `approveSchedule`. A save that moved the staging first leaves it `ready`.
 * `retryPdfIntakeSchedule` carries no `generation`: it re-reads the job's
 * whole file as the reviewer, for a `ready` or `failed` job with a retryable
 * failed page, starting a new staging, so edits made on the earlier one are
 * discarded; it appends `retry`. `startPdfIntakeSchedule` runs a file the
 * caller uploaded to their own `fileFolder` (`temp/<uid>/<fileFolderId>`)
 * through `runPdfIntakeSchedule` for `order`, as the caller; the job id is the
 * file's SHA-256, so the same bytes sent again open the earlier job and run
 * nothing unless that job may be re-claimed. Its optional `source` names the
 * attachment version the bytes were read from and is stored on a new job only.
 * `dismissPdfIntakeJob` sets a `ready` or `failed` schedule job `dismissed`,
 * keeps the status it left in `dismissedFrom` and appends `dismiss`;
 * `restorePdfIntakeJob` returns a `dismissed` job to that status, clears
 * `dismissedFrom` and appends `restore`. Neither carries `generation`, and
 * both refuse a pricebook job.
 */
export type PdfIntakeTaskRequest =
  | { taskType: 'savePdfIntakeDecisions'; jobId: string; generation: string; decisions: ProcessDocumentDecisions }
  | { taskType: 'approvePdfIntakeSchedule'; jobId: string; generation: string }
  | { taskType: 'retryPdfIntakeSchedule'; jobId: string }
  | { taskType: 'dismissPdfIntakeJob'; jobId: string }
  | { taskType: 'restorePdfIntakeJob'; jobId: string }
  | { taskType: 'startPdfIntakeSchedule'; fileFolder: string; filename: string; order: PdfIntakeOrderRef; sourceTypeId: ExtractionSourceTypeId; source?: PdfIntakeJobSource };

export type PdfIntakeTaskResult = { success: true } | { success: false; error: string };

/** What `startPdfIntakeSchedule` answers: the job the file opened, run to the end, or the earlier job for the same bytes, untouched. */
export type StartPdfIntakeScheduleResult =
  | { success: true; jobId: string; status: PdfIntakeJobStatus; route: PdfIntakeRoute | null }
  | { success: false; error: string };

/** What the agent's `startPdfIntake` tool answers: the job the PDF opened, run to the end, or the earlier job for the same bytes (`alreadyRead`). `href` is the schedule review's relative path. */
export type StartPdfIntakeToolResult = {
  jobId: string;
  status: PdfIntakeJobStatus;
  route: PdfIntakeRoute | null;
  pages: number | null;
  flaggedCells: number;
  failedPages: PdfIntakePageFailure[];
  alreadyRead: boolean;
  href: string;
};
